import * as d3 from 'd3';
import moment, {Moment, MomentInput} from 'moment';
import _ from 'underscore';

import {assert} from 'Asserts';
import {
  CalendarFile,
  CalendarInteraction,
  CalendarItem,
  DragCompleteCalendarInteraction,
  DraggingCalendarInteraction,
  InactiveCalendarInteraction,
  isCalendarFile,
  isCalendarProcessTask,
  isCalendarProject,
  isCalendarRequest,
  LayeredCalendarItem,
  LayeredCalendarItems,
  ProjectColorMapping,
  SelectedItemCalendarInteraction,
  ViewRange,
} from 'calendar/CalendarConstants';
import {SourceSummary} from 'constants/SourceConstants';
import {UserSummary} from 'constants/UserConstants';
import * as colors from 'lib/Colors';
import {unpackRequestAssignees} from 'requests/RequestCalendarUtils';
import {NO_AVATAR_URL} from 'ui-library/avatar/Avatar';
import * as DateUtil from 'util/DateUtil';

import {SourceSummaryGraphQL} from '../../graphql-fragments/nav/sources/__generated/SourceSummaryGraphQL';
import {getCalendarItemProject} from './CalendarConstants';

export function isInactiveInteraction(
  interact: CalendarInteraction
): interact is InactiveCalendarInteraction {
  return (
    !interact.hasOwnProperty('mouseDownDate') &&
    !interact.hasOwnProperty('mouseOverDate') &&
    !interact.hasOwnProperty('mouseUpCoords') &&
    !interact.hasOwnProperty('selectedCalendarItemDay')
  );
}

export function isDraggingInteraction(
  interact: CalendarInteraction
): interact is DraggingCalendarInteraction {
  return (
    interact.hasOwnProperty('mouseDownDate') &&
    interact.hasOwnProperty('mouseOverDate') &&
    !interact.hasOwnProperty('mouseUpCoords') &&
    !interact.hasOwnProperty('selectedCalendarItemDay')
  );
}

export function isDragCompleteInteraction(
  interact: CalendarInteraction
): interact is DragCompleteCalendarInteraction {
  return (
    interact.hasOwnProperty('mouseDownDate') &&
    interact.hasOwnProperty('mouseOverDate') &&
    interact.hasOwnProperty('mouseUpCoords') &&
    !interact.hasOwnProperty('selectedCalendarItemDay')
  );
}

export function isSelectedItemInteraction(
  interact: CalendarInteraction
): interact is SelectedItemCalendarInteraction {
  return (
    !interact.hasOwnProperty('mouseDownDate') &&
    !interact.hasOwnProperty('mouseOverDate') &&
    !interact.hasOwnProperty('mouseUpCoords') &&
    interact.hasOwnProperty('selectedCalendarItemDay')
  );
}

interface UserSummaryNullableAvatar {
  id: string;
  handle: string;
  avatar_url: string | null;
}

function addAvatarUrl(userSummary: UserSummaryNullableAvatar): UserSummary {
  return {
    ...userSummary,
    avatar_url: userSummary.avatar_url ?? NO_AVATAR_URL,
  };
}

const CalendarUtil = {
  isRangeOverlapping(firstStart: Moment, firstEnd: Moment, secondStart: Moment, secondEnd: Moment): boolean {
    return (
      CalendarUtil.isDateBetween(firstStart, secondStart, secondEnd) ||
      CalendarUtil.isDateBetween(firstEnd.clone().subtract(1, 'days'), secondStart, secondEnd) ||
      CalendarUtil.isDateBetween(secondStart, firstStart, firstEnd) ||
      CalendarUtil.isDateBetween(secondEnd.clone().subtract(1, 'days'), firstStart, firstEnd)
    );
  },

  // Inclusive on start and exclusive on end.
  isDateBetween(date: Moment, start: Moment, end: Moment): boolean {
    return date.isSame(start) || date.isBetween(start, end);
  },

  getProjectColorAlternativeTint(color: string, index: number): string {
    const step = 0.2;
    const hsl = d3.hsl(color);
    const newS = hsl.s + index * step;

    // Wrap-around when we reach max saturation point (1), but start over at 0.2
    const wrappedS = newS === 1 ? 1 : ((newS - 0.2) % 0.8) + 0.2;
    return d3.hsl(hsl.h, wrappedS, hsl.l).formatHex();
  },

  getProjectColorMapping(projects: Array<SourceSummary>): ProjectColorMapping {
    const projectsByColor = projects.reduce(
      (memo, project) => {
        const color = colors.stringToPastelColor(project.id);
        memo[color] = (memo[color] ?? []).concat([project.id]);
        return memo;
      },
      {} as {[color: string]: Array<string>}
    );

    const projectColorMapping: ProjectColorMapping = {};

    Object.entries(projectsByColor).forEach(([color, projectIds]) => {
      projectIds.forEach((projectId, index) => {
        projectColorMapping[projectId] = this.getProjectColorAlternativeTint(color, index);
      });
    });

    return projectColorMapping;
  },

  getCalenderItemProject(item: CalendarItem): SourceSummary | SourceSummaryGraphQL {
    return isCalendarProject(item) ? item : getCalendarItemProject(item)!;
  },

  sortCalendarItems<T extends CalendarItem>(items: Array<T>): Array<T> {
    return _.sortBy(items, (item) => item.name.toLowerCase());
  },

  sortFiles(files: Array<CalendarFile>): Array<CalendarFile> {
    const sortedFiles = _.sortBy(files, (file) => file.name.toLowerCase());
    return _.sortBy(sortedFiles, (file) => file.endAccess);
  },

  rangeFromInteract(interact: DraggingCalendarInteraction | DragCompleteCalendarInteraction): ViewRange {
    let startDate = interact.mouseDownDate;
    let endDate = interact.mouseOverDate;
    if (startDate.isAfter(endDate)) {
      [startDate, endDate] = [endDate, startDate];
    }
    // Selected range starts at (start of startDate) and ends at (end of endDate).
    // Since range we're returning is exclusive, will return endDate + 1 day.
    endDate = endDate.clone().add(1, 'days');
    return [startDate, endDate];
  },

  // gets the date associated with the item.
  // Requests have a scheduledOn field whereas entries and "files" have a date field.
  getItemDate(item: CalendarItem | CalendarFile): Moment {
    if (isCalendarRequest(item)) {
      return item.scheduledOn;
    } else if (isCalendarFile(item)) {
      return moment(item.date);
    } else if (isCalendarProcessTask(item)) {
      assert(item.scheduledOnDate != null, "An unscheduled task shouldn't be displayed on the calendar");
      return moment(item.scheduledOnDate);
    } else {
      return item.date;
    }
  },

  // gets the number of days that the item spans.
  // numDays is only relevant for entries - for all other items this should return 1.
  getItemNumDays<T extends CalendarItem>(item: LayeredCalendarItem<T>): number {
    return item.numDays ?? 1;
  },

  // gets the relevant parties on the item
  getItemParties(item: CalendarItem): Array<UserSummary> {
    if (isCalendarRequest(item)) {
      return _.unique([item.requestor, ...unpackRequestAssignees(item.assignees)], ({id}) => id);
    } else if (isCalendarProcessTask(item)) {
      return _.unique(
        [
          ...(item.assignee != null ? [addAvatarUrl(item.assignee)] : []),
          ...item.process.watcherParties.map((watcher) => addAvatarUrl(watcher)),
        ],
        ({id}) => id
      );
    }
    return item.collaborators;
  },

  calendarItemsInWeek(
    layers: LayeredCalendarItems<CalendarItem>,
    weekStart: Moment
  ): LayeredCalendarItems<CalendarItem> {
    const _isItemOverlapping = (item: LayeredCalendarItem<CalendarItem>): boolean => {
      const itemStart = CalendarUtil.getItemDate(item);
      const itemEnd = itemStart.clone().add(CalendarUtil.getItemNumDays(item), 'days');
      const weekEnd = weekStart.clone().add(1, 'weeks');
      return CalendarUtil.isRangeOverlapping(weekStart, weekEnd, itemStart, itemEnd);
    };

    // Returns layered items, but filtering out items not in [weekStart, weekStart + 1 week)
    return layers.map((layer) => layer.filter((item) => _isItemOverlapping(item)));
  },

  readableDateRange(start: MomentInput, end: MomentInput, {showYear}: {showYear?: boolean} = {}): string {
    let dateString = DateUtil.readableTime(start, {showTime: false, showYear});
    if (!moment(start).isSame(moment(end), 'day')) {
      dateString += ` - ${DateUtil.readableTime(end, {showTime: false, showYear})}`;
    }
    return dateString;
  },

  hasCalendarItems(...calendarItemArrays: Array<Array<CalendarItem | CalendarFile>>): boolean {
    return calendarItemArrays.some((arr) => arr.length > 0);
  },
};

export default CalendarUtil;
